package io.jsonize.web.view

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.FormatIndentDecrease
import androidx.compose.material.icons.automirrored.filled.FormatIndentIncrease
import androidx.compose.material.icons.filled.AccountTree
import androidx.compose.material.icons.filled.ContentCopy
import androidx.compose.material.icons.filled.CopyAll
import androidx.compose.material.icons.filled.Copyright
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Download
import androidx.compose.material.icons.filled.SwapHoriz
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.State
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import io.jsonize.web.compare.calculateLineDiffs
import io.jsonize.web.components.TooltipIconButton
import io.jsonize.web.screens.editor.EditorTextArea
import io.jsonize.web.screens.editor.EditorView
import io.jsonize.web.screens.editor.isLikelyMinifiedJson
import io.jsonize.web.theme.LocalThemeIsDark
import io.jsonize.web.viewmodel.AppViewModel
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.EventListener

private const val AUTO_COMPARE_DELAY_MS = 350L

@Composable
fun CompareView(
    appViewModel: AppViewModel,
    leftEditorRef: State<EditorView?>,
    padding: Dp = 8.dp
) {
    val isDark by LocalThemeIsDark.current
    val mainText by appViewModel.text.collectAsState()
    val rightText by appViewModel.compareText.collectAsState()
    val rightDiffs by appViewModel.compareRightDiffs.collectAsState()

    val rightEditorRef = remember { mutableStateOf<EditorView?>(null) }

    DisposableEffect(Unit) {
        onDispose { appViewModel.clearCompareDiffs() }
    }

    LaunchedEffect(mainText, rightText) {
        if (mainText.isBlank() || rightText.isBlank()) {
            appViewModel.clearCompareDiffs()
            return@LaunchedEffect
        }
        delay(AUTO_COMPARE_DELAY_MS)
        if (mainText.isBlank() || rightText.isBlank()) {
            appViewModel.clearCompareDiffs()
            return@LaunchedEffect
        }
        val result = calculateLineDiffs(mainText, rightText)
        appViewModel.updateCompareDiffs(result.left, result.right)
    }

    DisposableEffect(leftEditorRef.value, rightEditorRef.value) {
        val left = leftEditorRef.value
        val right = rightEditorRef.value
        if (left == null || right == null) return@DisposableEffect onDispose { }

        val leftScroll = left.asDynamic().scrollDOM as? HTMLElement
        val rightScroll = right.asDynamic().scrollDOM as? HTMLElement
        if (leftScroll == null || rightScroll == null) return@DisposableEffect onDispose { }

        var syncing = false
        val leftListener = EventListener {
            if (syncing) return@EventListener
            syncing = true
            rightScroll.scrollTop = leftScroll.scrollTop
            rightScroll.scrollLeft = leftScroll.scrollLeft
            window.setTimeout({ syncing = false }, 0)
        }
        val rightListener = EventListener {
            if (syncing) return@EventListener
            syncing = true
            leftScroll.scrollTop = rightScroll.scrollTop
            leftScroll.scrollLeft = rightScroll.scrollLeft
            window.setTimeout({ syncing = false }, 0)
        }

        leftScroll.addEventListener("scroll", leftListener)
        rightScroll.addEventListener("scroll", rightListener)

        onDispose {
            leftScroll.removeEventListener("scroll", leftListener)
            rightScroll.removeEventListener("scroll", rightListener)
        }
    }

    Column(modifier = Modifier.fillMaxSize().padding(padding)) {
        /*Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = "Compare with main editor",
                style = MaterialTheme.typography.labelLarge
            )
            Spacer(Modifier.weight(1f))
            TextButton(onClick = {
                appViewModel.updateCompareText("")
                appViewModel.clearCompareDiffs()
            }) {
                Text("Clear")
            }
        }

        Spacer(Modifier.height(6.dp))*/

        EditorTextArea(
            text = rightText,
            onTextChange = { appViewModel.updateCompareText(it) },
            lineDiffs = rightDiffs,
            isStreaming = false,
            isDark = isDark,
            placeholderText = AppViewModel.DEFAULT_JSON_COMPARE_PLACEHOLDER,
            modifier = Modifier.weight(1f).fillMaxWidth(),
            onEditorReady = { rightEditorRef.value = it },
            onEditorDispose = { rightEditorRef.value = null }
        )

        /*if (mainText.isBlank()) {
            Spacer(Modifier.height(6.dp))
            Text(
                text = "Main editor is empty. Enter JSON on the left to compare.",
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodySmall
            )
        }*/
    }
}

@Composable
fun ColumnScope.CompareToolbar(
    text: String,
    format: () -> Unit,
    minify: () -> Unit,
    copy: () -> Unit,
    download:() -> Unit,
    clear: () -> Unit,
    swap: () -> Unit
){

    val isMinified = isLikelyMinifiedJson(text)
    val formatIcon = if (isMinified) {
        Icons.AutoMirrored.Default.FormatIndentIncrease
    } else {
        Icons.AutoMirrored.Default.FormatIndentDecrease
    }
    val formatDescription = if (isMinified) "Format" else "Minify"

    TooltipIconButton(
        tooltip = formatDescription,
        onClick = { if (isMinified) format() else minify() }
    ) {
        Icon(
            imageVector = formatIcon,
            contentDescription = formatDescription
        )
    }

    TooltipIconButton(
        tooltip = "Swap",
        onClick = swap
    ) {
        Icon(
            imageVector = Icons.Default.SwapHoriz,
            contentDescription = "Swap"
        )
    }

    TooltipIconButton(
        tooltip = "Copy",
        onClick = copy
    ) {
        Icon(
            imageVector = Icons.Default.ContentCopy,
            contentDescription = "Copy"
        )
    }

    TooltipIconButton(
        tooltip = "Download",
        onClick = download
    ) {
        Icon(
            imageVector = Icons.Default.Download,
            contentDescription = "Download"
        )
    }

    TooltipIconButton(
        tooltip = "Clear",
        onClick = clear
    ) {
        Icon(
            imageVector = Icons.Default.Delete,
            contentDescription = "Clear"
        )
    }

}
